import PhysiotherapyPage from "./pages/physio/PhysiotherapyPage";
import ProceduresPage from "./pages/ProceduresPage";
import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { SiteProvider } from "./context/SiteContext";
import { LiveProvider, LiveIndicator } from "./lib/useLiveEvents";
import { FeatureProvider, FeatureRouteGuard } from "./context/FeatureContext";
import CmsPage, { LegalPage, SeoManager } from "./pages/CmsPage";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import MobileBottomNav from "./components/MobileBottomNav";
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import OmniSearchModal from "./components/OmniSearchModal";
import LoginPage, { ForgotPasswordPage } from "./pages/auth/PatientLoginPage";
import RegisterPage, { AdminLoginPage } from "./pages/auth/RegisterPage";
import AccountArea, { RequireRole } from "./pages/AccountArea";
import AdminLayout from "./components/admin/AdminLayout";
import { PharmacyCartProvider, MedicinesHome, MedicineList, ProductPage } from "./pages/pharmacy/PharmacyStore";
import { CartPage, CheckoutPage, UploadPrescriptionPage } from "./pages/pharmacy/PharmacyCheckout";
import { MembershipLanding, ComparePage, PlanDetailPage, PurchasePage, SuccessPage } from "./pages/membership/MembershipPages";
import { ConsultHome, DoctorsList, DoctorProfile, BookPage, BookingSuccess } from "./pages/consult/ConsultPublic";
import { VideoRoom } from "./pages/consult/PatientConsult";
import DoctorPortal, { DoctorLoginPage } from "./pages/doctor/DoctorPortal";
import DoctorJoinPage from "./pages/doctor/DoctorJoinPage";
import NotFoundPage from "./pages/NotFoundPage";
import StaffPortal, { StaffLoginPage } from "./pages/staff/StaffPortal";
import DiagnosticBookingModal from "./components/DiagnosticBookingModal";
import ReportViewerModal from "./components/ReportViewerModal";
import { Toaster } from "sonner";

import HomePage from "./pages/HomePage";
import DiagnosticsPage from "./pages/DiagnosticsPage";
import PackagesPage from "./pages/PackagesPage";
import CardiologyPage from "./pages/CardiologyPage";
import ForumPage from "./pages/ForumPage";
import ForumThreadPage from "./pages/ForumThreadPage";
import HealthLibraryPage from "./pages/HealthLibraryPage";
import ReviewsPage from "./pages/ReviewsPage";
import HealthArticlePage from "./pages/HealthArticlePage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AboutContactPage from "./pages/AboutContactPage";
import CorporatePage from "./pages/CorporatePage";
import CorporatePortalPage from "./pages/CorporatePortalPage";
import CorporateHrPortal, { CorporateLoginPage } from "./pages/CorporateHrPortal";

import ActivatePage from "./pages/staff/ActivatePage";

// staff. / doctor. / admin. subdomains serve only their portal; public site is hidden there. Paths keep working everywhere.
export const PORTAL_HOST = (() => { const h = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase(); return h.startsWith("staff.") ? "staff" : h.startsWith("doctor.") ? "doctor" : h.startsWith("admin.") ? "admin" : null; })();

function AdminArea() {
  return (
    <Routes>
      <Route path="/staff/activate/:token" element={<ActivatePage portal="staff" />} />
      <Route path="/doctor/activate/:token" element={<ActivatePage portal="doctor" />} />
      {PORTAL_HOST && <Route path="/" element={<Navigate to={`/${PORTAL_HOST}${PORTAL_HOST === "admin" ? "/dashboard" : ""}`} replace />} />}
      {PORTAL_HOST && <Route path="*" element={<Navigate to={`/${PORTAL_HOST}/login`} replace />} />}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/staff/login" element={<StaffLoginPage />} />
      <Route path="/staff/*" element={<StaffPortal />} />
      <Route path="/doctor/login" element={<DoctorLoginPage />} />
      <Route path="/doctor/*" element={<DoctorPortal />} />
      <Route path="/admin/*" element={<RequireRole portal="admin" roles={["admin", "super_admin", "pharmacy_staff", "membership_staff", "receptionist", "finance", "operations_manager", "finance_manager", "content_manager", "auditor", "center_manager"]} redirect="/admin/login"><AdminLayout><AdminDashboardPage /></AdminLayout></RequireRole>} />
    </Routes>
  );
}

function PublicArea() {
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const [activeTestBooking, setActiveTestBooking] = useState(null);
  const [activeReportViewer, setActiveReportViewer] = useState(null);

  const handleOpenDoctorBooking = (doctor) => navigate(doctor && doctor.id ? `/doctors/${doctor.slug || doctor.id}` : "/doctors");
  const handleOpenTestBooking = (test) => setActiveTestBooking(test);
  const handleOpenReport = (report) => setActiveReportViewer(report);

  return (
    <div className="min-h-screen flex flex-col bg-[#FBF9F4] text-[#192621]">
      <Navbar onOpenSearch={() => setSearchOpen(true)} onBookClick={() => handleOpenDoctorBooking({ _picker: true })} />

      <main className="flex-1">
        <FeatureRouteGuard>
        <Routes>
          <Route path="/" element={<CmsPage slug="home" onBookDoctor={handleOpenDoctorBooking} onBookTest={handleOpenTestBooking} />} />
          <Route path="/doctors" element={<DoctorsList />} />
          <Route path="/consult" element={<ConsultHome />} />
          <Route path="/doctors/join" element={<DoctorJoinPage />} />
          <Route path="/doctors/register" element={<Navigate to="/doctors/join" replace />} />
          <Route path="/doctors/:slug" element={<DoctorProfile />} />
          <Route path="/book/:slug" element={<BookPage />} />
          <Route path="/consult/success/:id" element={<BookingSuccess />} />
          <Route path="/consult/video/:id" element={<VideoRoom />} />
          <Route path="/physiotherapy" element={<PhysiotherapyPage />} />
          <Route path="/procedures" element={<ProceduresPage />} />
          <Route path="/procedures/:pid" element={<ProceduresPage />} />
          <Route path="/diagnostics" element={<DiagnosticsPage onSelectTest={handleOpenTestBooking} />} />
          <Route path="/packages" element={<PackagesPage />} />
          <Route path="/cardiology" element={<CardiologyPage onBookDoctor={handleOpenDoctorBooking} onBookTest={handleOpenTestBooking} />} />
          <Route path="/my-health" element={<Navigate to="/account/dashboard" replace />} />
          <Route path="/forum" element={<ForumPage />} />
          <Route path="/forum/:id" element={<ForumThreadPage />} />
          <Route path="/health-library" element={<HealthLibraryPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/health-library/:slug" element={<HealthArticlePage />} />
          <Route path="/about" element={<CmsPage slug="about" onBookDoctor={handleOpenDoctorBooking} onBookTest={handleOpenTestBooking} />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/account/*" element={<AccountArea onOpenReport={handleOpenReport} onBookDoctor={handleOpenDoctorBooking} />} />
          <Route path="/medicines" element={<MedicinesHome />} />
          <Route path="/medicines/search" element={<MedicineList />} />
          <Route path="/medicines/product/:slug" element={<ProductPage />} />
          <Route path="/medicines/cart" element={<CartPage />} />
          <Route path="/medicines/checkout" element={<CheckoutPage />} />
          <Route path="/upload-prescription" element={<UploadPrescriptionPage />} />
          <Route path="/membership" element={<MembershipLanding />} />
          <Route path="/membership/compare" element={<ComparePage />} />
          <Route path="/membership/buy/:slug" element={<PurchasePage />} />
          <Route path="/membership/success/:id" element={<SuccessPage />} />
          <Route path="/membership/:slug" element={<PlanDetailPage />} />
          <Route path="/corporate" element={<CorporatePage />} />
          <Route path="/services" element={<CmsPage slug="services" onBookDoctor={handleOpenDoctorBooking} onBookTest={handleOpenTestBooking} />} />
          <Route path="/contact" element={<CmsPage slug="contact" onBookDoctor={handleOpenDoctorBooking} onBookTest={handleOpenTestBooking} />} />
          <Route path="/pages/:slug" element={<CmsPage onBookDoctor={handleOpenDoctorBooking} onBookTest={handleOpenTestBooking} />} />
          <Route path="/legal/:slug" element={<LegalPage />} />
          <Route path="/corporate/portal" element={<CorporateHrPortal />} />
          <Route path="/corporate/login" element={<CorporateLoginPage />} />
          <Route path="/corporate/portal/legacy" element={<CorporatePortalPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </FeatureRouteGuard>
      </main>

      <Footer />
      <FloatingWhatsApp onBookDoctor={handleOpenDoctorBooking} onInquireTest={handleOpenTestBooking} />
      <MobileBottomNav onBookClick={() => handleOpenDoctorBooking({ _picker: true })} />
      <OmniSearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} onSelectDoctor={handleOpenDoctorBooking} onSelectTest={handleOpenTestBooking} />
      <DiagnosticBookingModal test={activeTestBooking} isOpen={!!activeTestBooking} onClose={() => setActiveTestBooking(null)} />
      <ReportViewerModal report={activeReportViewer} isOpen={!!activeReportViewer} onClose={() => setActiveReportViewer(null)} />
    </div>
  );
}

function AppShell() {
  const { pathname } = useLocation();
  const isAdmin = pathname === "/admin" || pathname.startsWith("/admin/") || pathname === "/doctor" || pathname.startsWith("/doctor/") || pathname === "/staff" || pathname.startsWith("/staff/") || !!PORTAL_HOST;
  return (
    <>
      <SeoManager />
      {isAdmin ? <AdminArea /> : <PublicArea />}
      <Toaster position="top-right" richColors closeButton offset={{ top: isAdmin ? 72 : 140, right: 16 }} mobileOffset={{ top: isAdmin ? 64 : 120, right: 12, left: 12 }} toastOptions={{ style: { fontSize: "13px", borderRadius: "14px" } }} />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <LiveProvider>
      <FeatureProvider>
      <SiteProvider>
        <PharmacyCartProvider>
        <BrowserRouter>
          <AppShell />
          <LiveIndicator />
        </BrowserRouter>
        </PharmacyCartProvider>
      </SiteProvider>
      </FeatureProvider>
      </LiveProvider>
    </AuthProvider>
  );
}
